import axios, { AxiosResponse } from "axios";
import { ethers } from "ethers";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import CollectibleNFTArtifact from "../artifacts/contracts/CollectibleNFT.sol/CollectibleNFT.json";
import SongDTO from "../common/dto/SongDTO";
import SimpleButton from "../components/Buttons/SimpleButton";
import Column from "../components/Column/Column";
import { NavBar } from "../components/NavBar";
import RecordProgressBar from "../components/ProgressBar/RecordProgressBar";
import Row from "../components/Row/Row";
import Text from "../components/Text/Text";
import { Timeline } from "../components/Timeline/Timeline";
import { useAuth } from "../hooks/useAuth";
import error_record from "../imgs/error_record.svg";
import light_minting_progress_bg from "../imgs/light_minting_progress_bg.png";
import { getSelectedProvider, sleep } from "../utils";
import minting_progress_bg from "./imgs/minting_progress_page_bg.png";

interface DeployCollectibleNFTProps {
  theme: string;
  toggleTheme: () => void;
}
interface DeployCollectibleNFTState {
  songDto: SongDTO;
  timelineEvents: Array<string>;
  listLater: boolean;
}

const MintingProgressPageContainer = styled.div`
  display: flex;
  flex-direction: column;
  background: url(${(props) =>
      props.theme.themeType === "dark"
        ? minting_progress_bg
        : light_minting_progress_bg})
    no-repeat center;
  background-size: cover;
  width: 100vw;
  height: 100vh;
  justify-content: center;
  align-items: center;
  position: relative;
`;

const BigLetter = styled.h1`
  font-size: 150px;
  padding: 10px;
`;

const ActionContainer = styled.div`
  position: absolute;
  bottom: 25%;
  background: #9d7cff;
  padding: 1%;
  opacity: 0.9;
  border-radius: 16px;
  margin-top: 5%;
`;

const ErrorContainer = styled(Column)`
  position: absolute;
  bottom: 20%;
  background: #932b95;
  opacity: 0.9;
  border-radius: 16px;
  padding-block: 2%;
  padding-inline: 5%;
  margin-top: 5%;
`;

export const DeployCollectibleNFTPage: React.FC<DeployCollectibleNFTProps> = ({
  theme,
  toggleTheme,
}) => {
  const [loading, setLoading] = useState(true);
  const [progressText, setProgressText] = useState("");
  const [error, setError] = useState(false);
  const [showAction, setShowAction] = useState(false);
  const { state } = useLocation();
  const locationState = state as DeployCollectibleNFTState;
  const navigate = useNavigate();
  const auth = useAuth();

  useEffect(() => {
    deployNFTContract();
  }, []);

  const deployNFTContract = () => {
    const songDto = locationState.songDto;
    if (auth && auth.user && window.ethereum) {
      const selectedProvider = getSelectedProvider();

      const provider = new ethers.providers.Web3Provider(selectedProvider);
      const contract = new ethers.ContractFactory(
        CollectibleNFTArtifact.abi,
        CollectibleNFTArtifact.bytecode,
        provider.getSigner()
      );

      deploy(songDto, provider, contract);
    }
  };

  const deployContract = async (contract, contractName, contractSymbol, baseURI) => {
    if (locationState.listLater) {
      // Start minting available at designated time
      return await contract.deploy(
        contractName,
        contractSymbol,
        locationState.songDto.quantity,
        baseURI,
        locationState.songDto.launchDate,
        ethers.utils.parseEther(locationState.songDto.price),
        process.env.REACT_APP_COLLECTIBLE_NFT_REVENUE_FEE,
        process.env.REACT_APP_COMPOSENFT_BANK_ADDRESS
      );
    } else {
      return await contract.deploy(
        contractName,
        contractSymbol,
        locationState.songDto.quantity,
        baseURI,
        0,
        ethers.utils.parseEther(locationState.songDto.price),
        process.env.REACT_APP_COLLECTIBLE_NFT_REVENUE_FEE,
        process.env.REACT_APP_COMPOSENFT_BANK_ADDRESS
      );
    }
  };


  const deploy = async (songDto: SongDTO, provider, contract) => {

    const baseURI = await uploadToIpfs(songDto);

    setProgressText("Deploying Smart Contract");
    setShowAction(true);
    let resContract = null;
    try {
      resContract = await deployContract(
        contract,
        songDto.songName,
        songDto.songName.substring(0, 3),
        baseURI
      );
    } catch (error) {
      if (error.code === "INSUFFICIENT_FUNDS") {
        setProgressText("Insufficient funds to deploy contract");
      } else if (error.code === "ACTION_REJECTED") {
        setProgressText("Rejected transaction, please try again");
      } else {
        setProgressText("Error deploying contract, please try again");
      }
      setError(true);
      return;
    }

    // Check every 5 seconds to see if the contract has been deployed
    while (true) {
      setProgressText("Thanks! Continuing to deploy smart contract");
      setShowAction(false);
      await sleep(2000);
      const currentTransaction = await provider.getTransaction(
        resContract.deployTransaction.hash
      );
      if (
        currentTransaction === null ||
        currentTransaction.blockNumber !== null
      ) {
        // Transaction has been deployed when there is a block number associated with it
        break;
      }
    }


    const formData = new FormData();
    formData.append("wallet_address", auth.user.wallet_address);
    if (songDto.songCoverImg) {
      formData.append("song_cover", songDto.songCoverImg);
    }
    formData.append("song_name", songDto.songName);
    formData.append("description", songDto.description);
    formData.append("nft_address", resContract.address);
    formData.append("price", songDto.price);
    formData.append("song", songDto.songFile);
    formData.append("quantity", songDto.quantity);
    formData.append("is_master_copy", "false");
    formData.append("spotify_url", songDto.spotifyUrl);
    formData.append("youtube_url", songDto.youtubeUrl);
    formData.append("full_song_length", songDto.fullSongLength.toString());
    formData.append("metadata_base_url", baseURI);

    formData.append(
      "launch_date",
      songDto.launchDate ? songDto.launchDate.toString() : "0"
    );
    axios
      .post(
        process.env.REACT_APP_BACKEND_URL + "/add_song_collection",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      )
      .then((response: AxiosResponse) => {
        setProgressText(
          "Successfully deployed smart contract, fans can now mint your songs!"
        );
        setLoading(false);
      })
      .catch(() => {
        setError(true);
        setProgressText("Sorry, there was an error. Please try again ");
      });
  };

  const uploadToIpfs = async (songDto: SongDTO) => {
    setProgressText("Uploading song to IPFS");

    const formData = new FormData();
    const songFilename = songDto.songFile.name.replaceAll(' ', '_');
    const songCoverFilename = songDto.songCoverImg.name.replaceAll(' ', '_');
    formData.append(songFilename, songDto.songFile);
    formData.append('song_file_name', songFilename);
    formData.append("song_cover_file_name", songCoverFilename);
    formData.append(songCoverFilename, songDto.songCoverImg);

    formData.append("name", songDto.songName);
    formData.append("description", songDto.description);
    formData.append("max_tokens", songDto.quantity);

    const response = await axios.post(process.env.REACT_APP_BACKEND_URL +  '/create_nft_metadata', formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      }
    })
    return response.data.ipfs_metadata_link;
  };

  const onRetryClick = () => {
    setError(false);
    deployNFTContract();
  };

  const onFinishClicked = () => {
    navigate("/user/" + auth.user.wallet_address);
  };
  return (
    <>
      <NavBar theme={theme} themeToggler={toggleTheme} hideNavLinks={true} />
      <MintingProgressPageContainer>
        <Timeline
          events={locationState.timelineEvents}
          currentEventIndex={3}
          customCurrentTitle={"Uploading"}
          header={"Creating Collectible NFT"}
        />
        {error ? (
          <>
            <Row style={{ maxHeight: "15vh" }}>
              <BigLetter>L</BigLetter>
              <img src={error_record} style={{ width: "200px" }} />

              <BigLetter>A</BigLetter>
              <BigLetter>D</BigLetter>
              <BigLetter>I</BigLetter>
              <BigLetter>N</BigLetter>
              <BigLetter>G</BigLetter>
            </Row>
            <ErrorContainer>
              <Text>{progressText}</Text>
              <SimpleButton
                onClick={onRetryClick}
                style={{
                  maxWidth: "50%",
                  marginLeft: "auto",
                  marginRight: "auto",
                }}
              >
                Retry
              </SimpleButton>
            </ErrorContainer>
          </>
        ) : (
          <>
            <Row style={{ maxHeight: "15vh" }}>
              <BigLetter>L</BigLetter>
              <RecordProgressBar loading={loading} strokeWidth={4} />
              <BigLetter>A</BigLetter>
              <BigLetter>D</BigLetter>
              <BigLetter>I</BigLetter>
              <BigLetter>N</BigLetter>
              <BigLetter>G</BigLetter>
            </Row>

            <Text>{progressText}</Text>
            {showAction && (
              <ActionContainer>
                Please sign the transaction in wallet to continue
              </ActionContainer>
            )}
            {!loading && (
              <SimpleButton onClick={onFinishClicked}>Finish</SimpleButton>
            )}
          </>
        )}
      </MintingProgressPageContainer>
    </>
  );
};
